import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
    columnPinningFeature,
    columnVisibilityFeature,
    constructRow,
    createSortedRowModel,
    functionalUpdate,
    rowSelectionFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_basic,
    sortFn_datetime,
    sortFn_text,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import { cx } from '../ui/classNames';
import { escapeAttributeValue } from './dom';
import {
    SELECT_COLUMN,
    SELECT_COLUMN_ID,
    buildColumnLayout,
    buildHeaderRows,
} from './columnLayout';
import DataTableHeader from './DataTableHeader';
import {
    DataTableRow,
    EmptyRows,
    SkeletonRows,
    SummaryRow,
    renderTextCell,
} from './DataTableBody';
import DataTableFrame from './DataTableFrame';
import {
    bodyGroupClassName,
    bottomSummaryGroupClassName,
    clickableRowClassName,
    emptyCellClassName,
    frameClassName,
    rowClassName,
    rowStateClassNames,
    summaryRowClassName,
    tableClassName,
    tableLayoutClassNames,
    topSummaryGroupClassName,
} from './tableClasses';

const EMPTY_ARRAY = Object.freeze([]);
const DEFAULT_LOADING_ROW_COUNT = 8;
const dataTableFeatures = tableFeatures({
    columnVisibilityFeature,
    columnPinningFeature,
    rowSortingFeature,
    rowSelectionFeature,
    sortedRowModel: createSortedRowModel(),
    // What `sortFn: 'auto'` (TanStack's default) may pick from the values.
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        basic: sortFn_basic,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});
// Every column without its own setting gets: its value as text (with a
// native title when cut off), and blank values passed to its sort function,
// which places them (first in ascending order, as DataGrid did; see
// sorting.js). TanStack's default would sort undefined values last.
const DEFAULT_COLUMN = Object.freeze({
    cell: renderTextCell,
    sortUndefined: false,
});
// The table renders the sort indicators, the visible columns and the
// checkboxes; nothing else in TanStack's state.
const selectRenderState = (state) => ({
    sorting: state.sorting,
    columnVisibility: state.columnVisibility,
    rowSelection: state.rowSelection,
});
// Clicks and keys that start on these never activate the row.
const INTERACTIVE_SELECTOR = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    'label',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="switch"]',
    '[role="combobox"]',
].join(', ');
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const defaultGetRowId = (row, index) => row?.id ?? index;

// Focuses a cell's first focusable element, or the cell itself.
function focusCellIn(rowElement, columnId) {
    const cell = rowElement.querySelector(`td[data-field="${escapeAttributeValue(columnId)}"]`);
    if (!cell) return;
    const target = cell.querySelector(FOCUSABLE_SELECTOR) || cell;
    if (target === cell && !cell.hasAttribute('tabindex')) cell.setAttribute('tabindex', '-1');
    target.focus();
}

// A text cell cut off by its column shows its whole text as a native title.
function updateTextCellTitle(event) {
    const cell = event.target.closest?.('td[data-text-cell]');
    if (!cell) return;
    if (cell.scrollWidth > cell.clientWidth) cell.setAttribute('title', cell.textContent);
    else cell.removeAttribute('title');
}

/**
 * The app's table: a native <table> (thead, tbody, tfoot, colgroup) on
 * TanStack Table v9, styled with Tailwind to the app-style table look in
 * every theme (see tableClasses.js), inside one scroll container that is
 * also the frame, with a sticky header. No pagination: long tables scroll,
 * virtualized with `virtualize`.
 *
 * Data and columns
 * - data, columns: TanStack column defs (`id`, `header`, `accessorFn` or
 *   `accessorKey`, `cell`, `size`, `minSize`, `sortFn`, `sortDescFirst`,
 *   `sortUndefined`, `enableSorting`, `enableHiding`, nested `columns` for
 *   grouped headers). `header` and `cell` functions are called as plain
 *   render functions, so the elements they return keep their state when the
 *   defs are rebuilt; a function `header` renders inside the sort button, so
 *   it must hold no controls. A column without `cell` shows its value as
 *   text. Callbacks below receive a row's original data object.
 * - getRowId(row, index): the row's id (default `row.id`, else its index),
 *   read when `data` or `columns` change.
 * - column `meta`:
 *   - align: 'left' (default), 'center' or 'right', for header and cells
 *   - width: the column's width, px number or CSS string such as '43%'
 *     (default `size`, else 150); grow: true to take a share of the leftover
 *     width instead (minimum `minSize ?? size`)
 *   - cellClassName: string or fn(cellContext); a string is the column's
 *     look and also styles its loading skeleton cells (e.g.
 *     tableClasses.js optimizedCellClassName); cellStyle: fn(cellContext)
 *     returning inline styles (heat-map colours; in pinned and summary
 *     cells a background is laid over the row's, so they stay opaque)
 *   - headerClassName; description: header tooltip text; label: the
 *     column's name when `header` is not a string
 *   - headerControls: fn(headerContext) returning controls placed after the
 *     sort button in the header cell (they do not sort)
 *   - wrap: true for cells holding controls, which are not cut off with an
 *     ellipsis
 * - tableLayout: 'fixed' (default): a <colgroup> sets the widths, the table
 *   fills the frame and is at least as wide as its columns' minimums (a
 *   narrow frame scrolls sideways), and when every width is a fixed px width
 *   a filler column takes the rest. 'auto': columns sized to content, no
 *   pinning.
 * - 'aria-label' / 'aria-labelledby': the table's accessible name.
 * - wrapHeaders: header labels wrap onto a second line before they are cut
 *   off, for dense tables with long labels (the optimizer rule tables).
 *
 * Sorting: a sortable column's label is a button cycling ascending,
 * descending, unsorted (descending first with `sortDescFirst`), one column
 * at a time, with aria-sort on its header. Controlled with `sorting` +
 * `onSortingChange(nextSorting)`, else kept here from `initialSorting`.
 * `manualSorting` when the caller orders `data` itself.
 * `enableSortingRemoval={false}` drops the unsorted step: a sorted column
 * flips between its two directions (tables always shown in some order).
 *
 * Columns: `pinnedColumns` (ids; fixed px widths) stick to the left edge.
 * `columnVisibility` + `onColumnVisibilityChange(next)` control which
 * columns show (see ColumnVisibilityMenu).
 *
 * Rows
 * - striped (default true) stripes data rows by visual position; with false
 *   the caller stripes through getRowClassName.
 * - getRowClassName(row): extra row classes, e.g. a state tint; as the row's
 *   background it carries into pinned cells. Classes that must beat the
 *   kit's (a background over the stripe) take Tailwind's `!` prefix.
 * - onRowClick(row, event): rows become focusable and activate on click,
 *   Enter or Space, unless the event starts on a control inside the row.
 *   isRowClickable(row) limits which rows.
 * - selectedRowId: the highlighted row, with aria-selected.
 * - getRowProps(row): extra attributes and handlers for the <tr>
 *   (aria-label, aria-disabled, onMouseEnter…).
 * - renderFullWidthRow(row): a node renders that row as one cell spanning
 *   every column, unstriped (section headers, explanations).
 * - summaryRows { top, bottom }: plain row objects rendered with the
 *   columns, outside sorting and selection: `top` sticky under the header,
 *   `bottom` in a sticky <tfoot>.
 * - rowSelection + onRowSelectionChange(next) ({ [rowId]: true }):
 *   a leading checkbox column (pinned with the pinned columns). The header
 *   checkbox selects or clears the selectable rows of `data`, leaving other
 *   ids alone; enableRowSelection (bool or fn(row)) limits which rows. A row
 *   click never toggles selection. Ticked rows take the selected tint.
 *
 * State and size
 * - loading: skeleton rows (`loadingRowCount`, default enough to fill a
 *   fixed or maximum height, else 8) and aria-busy. emptyMessage: shown in
 *   one row when there are no rows. emptyRowCount adds decorative striped
 *   rows to an empty table, with no loading animation or selectable records.
 * - height (fixed), maxHeight (grows with rows up to it), or neither (grows
 *   with rows); `className` for anything else, e.g. 'h-full' to fill a flex
 *   parent. rowHeight (38) and headerHeight (40) are px, 8px less in compact
 *   mode.
 * - virtualize: render only the rows in view (rows of one height), for
 *   long lists.
 *
 * `ref` exposes scrollToRow(rowId, { focusColumnId }): scrolls the row into
 * view and focuses that cell's first control, or the cell; false for an
 * unknown row.
 *
 * Tests can find rows by `data-row-id` and header and body cells by
 * `data-field`.
 */
const DataTable = forwardRef(function DataTable({
    data,
    columns,
    getRowId = defaultGetRowId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    sorting,
    onSortingChange,
    initialSorting = EMPTY_ARRAY,
    manualSorting = false,
    enableSortingRemoval = true,
    pinnedColumns = EMPTY_ARRAY,
    columnVisibility,
    onColumnVisibilityChange,
    striped = true,
    getRowClassName,
    onRowClick,
    isRowClickable,
    selectedRowId,
    getRowProps,
    renderFullWidthRow,
    summaryRows,
    rowSelection,
    onRowSelectionChange,
    enableRowSelection = true,
    loading = false,
    loadingRowCount,
    emptyMessage,
    emptyRowCount = 0,
    height,
    maxHeight,
    className,
    rowHeight = 38,
    headerHeight = 40,
    wrapHeaders = false,
    tableLayout = 'fixed',
    virtualize = false,
}, ref) {
    const frameRef = useRef(null);
    const tableRef = useRef(null);
    const topSummaryRef = useRef(null);
    const bodyRef = useRef(null);
    const virtualizerRef = useRef(null);
    const pendingFocusRef = useRef(null);
    const latestRef = useRef(null);
    const autoLayout = tableLayout === 'auto';
    const selectable = rowSelection !== undefined;

    const tableColumns = useMemo(
        () => (selectable ? [SELECT_COLUMN, ...columns] : columns),
        [selectable, columns],
    );
    const resolveRowId = useCallback((row, index) => {
        const id = getRowId(row, index);
        return id == null ? String(index) : String(id);
    }, [getRowId]);
    // Accessor values are cached per row instance; rebuild the rows whenever
    // the column defs (and so their accessors) change. Row ids are read as
    // the rows are built, so an inline getRowId costs nothing.
    const tableData = useMemo(() => data.slice(), [data, tableColumns]);
    // Keyed by the ids, so a fresh array with the same ids keeps the layout.
    const pinnedKey = autoLayout ? '' : pinnedColumns.join('\u0000');
    const columnPinning = useMemo(() => ({
        start: pinnedKey ? [...(selectable ? [SELECT_COLUMN_ID] : []), ...pinnedKey.split('\u0000')] : EMPTY_ARRAY,
        end: EMPTY_ARRAY,
    }), [pinnedKey, selectable]);

    const state = { columnPinning };
    const options = {
        features: dataTableFeatures,
        columns: tableColumns,
        data: tableData,
        defaultColumn: DEFAULT_COLUMN,
        getRowId: resolveRowId,
        state,
        initialState: { sorting: initialSorting },
        // Every column sorts ascending first (TanStack would start numbers
        // descending), and one column at a time.
        sortDescFirst: false,
        enableMultiSort: false,
        enableSortingRemoval,
        manualSorting,
        enableRowSelection: typeof enableRowSelection === 'function'
            ? (row) => Boolean(enableRowSelection(row.original))
            : enableRowSelection,
    };
    // Controlled slices report plain next values.
    if (sorting !== undefined) {
        state.sorting = sorting;
        options.onSortingChange = (updater) => {
            const next = functionalUpdate(updater, sorting);
            if (next !== sorting) onSortingChange?.(next);
        };
    }
    if (columnVisibility !== undefined) {
        state.columnVisibility = columnVisibility;
        options.onColumnVisibilityChange = (updater) => {
            const next = functionalUpdate(updater, columnVisibility);
            if (next !== columnVisibility) onColumnVisibilityChange?.(next);
        };
    }
    if (selectable) {
        state.rowSelection = rowSelection;
        options.onRowSelectionChange = (updater) => {
            const next = functionalUpdate(updater, rowSelection);
            if (next !== rowSelection) onRowSelectionChange?.(next);
        };
    }
    const table = useTable(options, selectRenderState);

    const rowModel = table.getRowModel();
    const { rows } = rowModel;
    const headerGroups = table.getHeaderGroups();
    const leafHeaders = headerGroups[headerGroups.length - 1]?.headers ?? EMPTY_ARRAY;
    const pinnedCount = useMemo(() => {
        const pinned = new Set(columnPinning.start);
        let count = 0;
        while (count < leafHeaders.length && pinned.has(leafHeaders[count].column.id)) count += 1;
        return count;
    }, [leafHeaders, columnPinning]);
    const layout = useMemo(
        () => buildColumnLayout(leafHeaders, pinnedCount, autoLayout),
        [leafHeaders, pinnedCount, autoLayout],
    );
    const headerRows = useMemo(() => buildHeaderRows(headerGroups), [headerGroups]);
    const columnSpan = layout.entries.length + (layout.filler ? 1 : 0);

    // Summary rows are built against the table (so cells get the usual
    // context) but stay outside its row model.
    const coreTable = table.getAllLeafColumns()[0]?.table;
    const topOriginals = summaryRows?.top ?? EMPTY_ARRAY;
    const bottomOriginals = summaryRows?.bottom ?? EMPTY_ARRAY;
    const summary = useMemo(() => {
        const build = (originals, kind) => (coreTable ? originals.map(
            (original, index) => constructRow(coreTable, `${kind}-summary-${index}`, original, index, 0),
        ) : EMPTY_ARRAY);
        return { top: build(topOriginals, 'top'), bottom: build(bottomOriginals, 'bottom') };
    }, [coreTable, topOriginals, bottomOriginals, tableColumns]);

    // Full-width rows are not striped and do not count towards the stripes.
    const fullWidthContents = useMemo(
        () => (renderFullWidthRow ? rows.map((row) => renderFullWidthRow(row.original)) : null),
        [rows, renderFullWidthRow],
    );
    const stripeParities = useMemo(() => {
        if (!fullWidthContents) return null;
        let dataRowCount = 0;
        return fullWidthContents.map((content) => {
            if (content != null) return null;
            dataRowCount += 1;
            return (dataRowCount - 1) % 2;
        });
    }, [fullWidthContents]);

    let selectAll = null;
    if (selectable) {
        let selectableCount = 0;
        let selectedCount = 0;
        table.getCoreRowModel().rows.forEach((row) => {
            if (!row.getCanSelect()) return;
            selectableCount += 1;
            if (rowSelection[row.id]) selectedCount += 1;
        });
        const allSelected = selectableCount > 0 && selectedCount === selectableCount;
        selectAll = {
            checked: allSelected,
            indeterminate: selectedCount > 0 && !allSelected,
            disabled: selectableCount === 0,
            onChange: () => table.toggleAllRowsSelected(!allSelected),
        };
    }

    const highlightsRows = selectedRowId !== undefined;
    const renderRow = (row, index, rowRef) => {
        const { original } = row;
        const fullWidthContent = fullWidthContents ? fullWidthContents[index] : null;
        const highlighted = selectedRowId != null && row.id === String(selectedRowId);
        const clickable = Boolean(onRowClick) && (!isRowClickable || Boolean(isRowClickable(original)));
        const checked = selectable ? Boolean(rowSelection[row.id]) : undefined;
        let stateClassName = rowStateClassNames.even;
        if (highlighted) stateClassName = rowStateClassNames.selected;
        else if (fullWidthContent != null) stateClassName = rowStateClassNames.plain;
        else if (checked) stateClassName = rowStateClassNames.checked;
        else if (striped && (stripeParities ? stripeParities[index] : index % 2) === 1) {
            stateClassName = rowStateClassNames.odd;
        }
        return (
            <DataTableRow
                key={row.id}
                row={row}
                layout={layout}
                className={cx(
                    rowClassName,
                    stateClassName,
                    clickable && clickableRowClassName,
                    getRowClassName?.(original),
                )}
                ariaSelected={highlightsRows ? highlighted : undefined}
                clickable={clickable}
                checked={checked}
                canSelect={selectable ? row.getCanSelect() : undefined}
                fullWidthContent={fullWidthContent}
                columnSpan={columnSpan}
                rowProps={getRowProps?.(original)}
                dataIndex={rowRef ? index : undefined}
                rowRef={rowRef}
            />
        );
    };

    const headerBlockHeight = headerRows.length * headerHeight;
    const scrollMargin = headerBlockHeight + (loading ? 0 : summary.top.length * rowHeight);
    let frameHeight = null;
    if (typeof height === 'number') frameHeight = height;
    else if (typeof maxHeight === 'number') frameHeight = maxHeight;
    const initialHeight = frameHeight ?? (typeof window === 'undefined' ? 800 : window.innerHeight);
    const skeletonRowCount = loadingRowCount ?? (frameHeight == null
        ? DEFAULT_LOADING_ROW_COUNT
        : Math.max(1, Math.ceil((frameHeight - headerBlockHeight) / rowHeight)));

    // A row the caller asked to focus (scrollToRow) that the virtualizer had
    // not rendered yet: focused as it mounts.
    const onRowMount = useCallback((rowElement) => {
        const pending = pendingFocusRef.current;
        if (!pending || rowElement.getAttribute('data-row-id') !== pending.rowId) return;
        pendingFocusRef.current = null;
        focusCellIn(rowElement, pending.columnId);
    }, []);

    latestRef.current = {
        rows,
        virtualize,
        scrollMargin,
        initialHeight,
        stickyTopFallback: scrollMargin,
        stickyBottomFallback: loading ? 0 : summary.bottom.length * rowHeight,
    };
    useImperativeHandle(ref, () => {
        const findRowElement = (rowId) => bodyRef.current?.querySelector(
            `tr[data-row-id="${escapeAttributeValue(rowId)}"]`,
        ) || null;
        // Scrolls the frame just enough to show the row below the sticky
        // header and above the sticky footer.
        const scrollRowIntoView = (rowId, index) => {
            const latest = latestRef.current;
            const frame = frameRef.current;
            const tableElement = tableRef.current;
            const stickyTop = ((tableElement?.tHead?.offsetHeight || 0) + (topSummaryRef.current?.offsetHeight || 0))
                || latest.stickyTopFallback;
            const stickyBottom = tableElement?.tFoot?.offsetHeight || latest.stickyBottomFallback;
            const virtualizer = latest.virtualize ? virtualizerRef.current : null;
            let top = 0;
            let size = 0;
            if (virtualizer) {
                const item = virtualizer.measurementsCache[index];
                top = item ? item.start - latest.scrollMargin + stickyTop : 0;
                size = item?.size ?? 0;
            } else {
                const rowElement = findRowElement(rowId);
                top = rowElement?.offsetTop ?? 0;
                size = rowElement?.offsetHeight ?? 0;
            }
            const viewportHeight = frame.clientHeight || latest.initialHeight;
            let next = frame.scrollTop;
            if (top < frame.scrollTop + stickyTop) next = top - stickyTop;
            else if (top + size > frame.scrollTop + viewportHeight - stickyBottom) {
                next = top + size - viewportHeight + stickyBottom;
            }
            next = Math.max(0, next);
            if (next === frame.scrollTop) return;
            frame.scrollTop = next;
            // Render the new window now rather than on the browser's
            // asynchronous scroll event.
            if (virtualizer) frame.dispatchEvent(new Event('scroll'));
        };
        return {
            scrollToRow(rowId, { focusColumnId } = {}) {
                const id = String(rowId);
                const index = latestRef.current.rows.findIndex((row) => row.id === id);
                if (index < 0 || !frameRef.current) return false;
                scrollRowIntoView(id, index);
                if (focusColumnId != null) {
                    const rowElement = findRowElement(id);
                    if (rowElement) focusCellIn(rowElement, focusColumnId);
                    else pendingFocusRef.current = { rowId: id, columnId: focusColumnId };
                }
                return true;
            },
        };
    }, []);

    // Rows activate from one pair of handlers on the <tbody>.
    const rowFromEvent = (event) => {
        const body = event.currentTarget;
        const { target } = event;
        // Events from portals (popovers opened from a cell) bubble here too.
        if (typeof target.closest !== 'function' || !body.contains(target)) return null;
        const rowElement = target.closest('tr[data-row-id]');
        if (!rowElement) return null;
        const control = target.closest(INTERACTIVE_SELECTOR);
        if (control && rowElement.contains(control)) return null;
        const row = rowModel.rowsById[rowElement.getAttribute('data-row-id')];
        if (!row || (isRowClickable && !isRowClickable(row.original))) return null;
        return row;
    };
    const rowActivationHandlers = onRowClick ? {
        onClick: (event) => {
            const row = rowFromEvent(event);
            if (row) onRowClick(row.original, event);
        },
        onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            const row = rowFromEvent(event);
            if (!row) return;
            event.preventDefault();
            onRowClick(row.original, event);
        },
    } : null;

    let bodyRows = null;
    if (loading) {
        bodyRows = <SkeletonRows rowCount={skeletonRowCount} layout={layout} />;
    } else if (rows.length === 0) {
        bodyRows = <EmptyRows rowCount={emptyRowCount} layout={layout} striped={striped} />;
        if (emptyMessage != null) {
            bodyRows = (
                <>
                <tr className={cx(rowClassName, rowStateClassNames.plain)}>
                    <td colSpan={columnSpan} className={emptyCellClassName}>{emptyMessage}</td>
                </tr>
                {bodyRows}
                </>
            );
        }
    } else if (!virtualize) {
        bodyRows = rows.map((row, index) => renderRow(row, index));
    }
    const virtual = virtualize && !loading && rows.length > 0 ? {
        rows,
        renderRow,
        rowHeight,
        scrollMargin,
        initialHeight,
        columnSpan,
        onRowMount,
        virtualizerRef,
    } : null;

    const summaryClassName = cx(rowClassName, rowStateClassNames.plain, summaryRowClassName);
    const showSummary = !loading;
    const topSummary = showSummary && summary.top.length > 0 ? (
        <tbody ref={topSummaryRef} data-summary="top" className={topSummaryGroupClassName}>
            {summary.top.map((row, index) => (
                <SummaryRow
                    key={row.id}
                    kind="top"
                    row={row}
                    layout={layout}
                    className={summaryClassName}
                    stickyStyle={{
                        top: `calc(var(--tbl-head-h) * ${headerRows.length} + var(--tbl-row-h) * ${index})`,
                    }}
                />
            ))}
        </tbody>
    ) : null;
    const bottomSummary = showSummary && summary.bottom.length > 0 ? (
        <tfoot className={bottomSummaryGroupClassName}>
            {summary.bottom.map((row, index) => (
                <SummaryRow
                    key={row.id}
                    kind="bottom"
                    row={row}
                    layout={layout}
                    className={summaryClassName}
                    stickyStyle={{
                        bottom: `calc(var(--tbl-row-h) * ${summary.bottom.length - 1 - index})`,
                    }}
                />
            ))}
        </tfoot>
    ) : null;

    return (
        <DataTableFrame
            frameRef={frameRef}
            className={cx(frameClassName, className)}
            style={{
                '--tbl-row-base': `${rowHeight}px`,
                '--tbl-head-base': `${headerHeight}px`,
                height,
                maxHeight,
            }}
            tableProps={{
                ref: tableRef,
                'aria-label': ariaLabel,
                'aria-labelledby': ariaLabelledBy,
                'aria-busy': loading || undefined,
                className: cx(tableClassName, tableLayoutClassNames[autoLayout ? 'auto' : 'fixed']),
                style: autoLayout ? undefined : { minWidth: layout.minWidth },
                onMouseOver: updateTextCellTitle,
            }}
            colgroup={(
                <colgroup>
                    {layout.entries.map((entry) => (
                        <col key={entry.id} style={entry.width == null ? undefined : { width: entry.width }} />
                    ))}
                    {layout.filler && <col />}
                </colgroup>
            )}
            header={(
                <DataTableHeader headerRows={headerRows} layout={layout} selectAll={selectAll} wrap={wrapHeaders} />
            )}
            topSummary={topSummary}
            bottomSummary={bottomSummary}
            bodyProps={{ ref: bodyRef, className: bodyGroupClassName, ...rowActivationHandlers }}
            virtual={virtual}
        >
            {bodyRows}
        </DataTableFrame>
    );
});

DataTable.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    getRowId: PropTypes.func,
    'aria-label': PropTypes.string,
    'aria-labelledby': PropTypes.string,
    sorting: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool })),
    onSortingChange: PropTypes.func,
    initialSorting: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool })),
    manualSorting: PropTypes.bool,
    enableSortingRemoval: PropTypes.bool,
    pinnedColumns: PropTypes.arrayOf(PropTypes.string),
    columnVisibility: PropTypes.objectOf(PropTypes.bool),
    onColumnVisibilityChange: PropTypes.func,
    striped: PropTypes.bool,
    getRowClassName: PropTypes.func,
    onRowClick: PropTypes.func,
    isRowClickable: PropTypes.func,
    selectedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    getRowProps: PropTypes.func,
    renderFullWidthRow: PropTypes.func,
    summaryRows: PropTypes.shape({ top: PropTypes.array, bottom: PropTypes.array }),
    rowSelection: PropTypes.objectOf(PropTypes.bool),
    onRowSelectionChange: PropTypes.func,
    enableRowSelection: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    loading: PropTypes.bool,
    loadingRowCount: PropTypes.number,
    emptyMessage: PropTypes.node,
    emptyRowCount: PropTypes.number,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    rowHeight: PropTypes.number,
    headerHeight: PropTypes.number,
    wrapHeaders: PropTypes.bool,
    tableLayout: PropTypes.oneOf(['fixed', 'auto']),
    virtualize: PropTypes.bool,
};

export default DataTable;
